import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Loader2, Plus, Star, Trash2 } from "lucide-react";
import { clsx } from "clsx";
import { api } from "@/api/client";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TaxonomyNode {
  id: string;
  parent_id: string | null;
  kode: string | null;
  nama: string;
  deskripsi?: string | null;
  level: number;
  path: string;
  urutan: number;
  is_leaf: boolean;
  is_active: boolean;
  taxonomy_type: string;
  children?: TaxonomyNode[];
}

export interface SelectedNode {
  id: string;
  label: string;          // mis. "SDA / Minerba / Mineral Logam / Nikel / Bijih Nikel"
  is_primary: boolean;
  /**
   * Rantai kode dari level-1 (setelah skipRoot) sampai node terpilih.
   * Mis. ["1", "1.1", "1.1.1", "1.1.1.1", "1.1.1.1.1"] untuk Bijih Nikel.
   * Consumer dapat memakai kodes[0] untuk derive Jenis PNBP.
   */
  kodes: string[];
}

interface Props {
  /** taxonomy_type yang dipakai, default PNBP_DOMAIN */
  taxonomyType?: string;
  /** Daftar node yang sudah terpilih (m2m). */
  value: SelectedNode[];
  onChange: (next: SelectedNode[]) => void;
  /**
   * Hanya boleh menambah jika node terpilih adalah leaf (tidak punya children).
   * Default true — memaksa klasifikasi spesifik sampai daun.
   */
  leafOnly?: boolean;
  /** Sembunyikan tombol tambah multi (single-pick mode). */
  singleSelect?: boolean;
  /**
   * Jika tree hanya punya satu root (mis. node 'PNBP' tunggal), lewati root itu
   * dan gunakan children-nya sebagai level 1. Default false.
   */
  skipRoot?: boolean;
  disabled?: boolean;
}

// ── Tree flatten helper ───────────────────────────────────────────────────────

function flatten(nodes: TaxonomyNode[], acc: Map<string, TaxonomyNode>): Map<string, TaxonomyNode> {
  for (const n of nodes) {
    acc.set(n.id, n);
    if (n.children?.length) flatten(n.children, acc);
  }
  return acc;
}

/**
 * Bangun rantai ancestor (top → leaf) untuk node terpilih, berhenti tepat di
 * bawah ``stopAtId`` (mis. root yang di-skip). Jika ``stopAtId`` null,
 * sertakan semua ancestor.
 */
function ancestorChain(
  node: TaxonomyNode,
  byId: Map<string, TaxonomyNode>,
  stopAtId: string | null,
): TaxonomyNode[] {
  const chain: TaxonomyNode[] = [];
  let cur: TaxonomyNode | undefined = node;
  while (cur && cur.id !== stopAtId) {
    chain.unshift(cur);
    cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
  }
  return chain;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function TaxonomyCascadePicker({
  taxonomyType = "PNBP_DOMAIN",
  value,
  onChange,
  leafOnly = true,
  singleSelect = false,
  skipRoot = false,
  disabled = false,
}: Props) {
  const { data: rawTree = [], isLoading } = useQuery<TaxonomyNode[]>({
    queryKey: ["taxonomy-tree", taxonomyType],
    queryFn: () => api.get(`/taxonomy/tree?taxonomy_type=${taxonomyType}`),
  });

  // Jika skipRoot=true dan hanya ada satu root, treat children-nya sebagai level-1.
  const skippedRootId: string | null =
    skipRoot && rawTree.length === 1 ? rawTree[0].id : null;
  const tree: TaxonomyNode[] =
    skippedRootId ? rawTree[0].children ?? [] : rawTree;

  const byId = useMemo(() => flatten(rawTree, new Map<string, TaxonomyNode>()), [rawTree]);

  // Working cascade selection — array of node id per level (skipping null gaps).
  const [working, setWorking] = useState<string[]>([]);

  // Reset working when value changes externally (mode single).
  useEffect(() => {
    if (singleSelect && value.length === 0) setWorking([]);
  }, [singleSelect, value]);

  function childrenOf(parentId: string | null): TaxonomyNode[] {
    if (!parentId) return tree;
    const parent = byId.get(parentId);
    return parent?.children ?? [];
  }

  const cascadeLevels: { parentId: string | null; selectedId: string | null }[] = useMemo(() => {
    const levels: { parentId: string | null; selectedId: string | null }[] = [];
    let parentId: string | null = null;
    for (let i = 0; i < 6; i++) {
      const opts = childrenOf(parentId);
      if (opts.length === 0) break;
      const selectedId = working[i] ?? null;
      levels.push({ parentId, selectedId });
      if (!selectedId) break;
      parentId = selectedId;
    }
    return levels;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [working, byId, tree]);

  function handlePick(levelIdx: number, nodeId: string) {
    const next = working.slice(0, levelIdx);
    if (nodeId) next[levelIdx] = nodeId;
    setWorking(next);
  }

  const lastPickedId = working[working.length - 1];
  const lastPicked = lastPickedId ? byId.get(lastPickedId) : undefined;
  const lastHasChildren = !!lastPicked?.children?.length;
  const canAdd =
    !!lastPicked &&
    (!leafOnly || !lastHasChildren) &&
    !value.some((v) => v.id === lastPicked.id);

  function handleAdd() {
    if (!lastPicked) return;
    const chain = ancestorChain(lastPicked, byId, skippedRootId);
    const label = chain.map((n) => n.nama).join(" / ");
    const kodes = chain.map((n) => n.kode ?? "");
    const isFirst = value.length === 0;
    const entry: SelectedNode = { id: lastPicked.id, label, is_primary: isFirst, kodes };
    const next: SelectedNode[] = singleSelect ? [{ ...entry, is_primary: true }] : [...value, entry];
    onChange(next);
    setWorking([]);
  }

  function handleRemove(id: string) {
    const next = value.filter((v) => v.id !== id);
    // Pastikan ada yang primary kalau masih ada item tersisa.
    if (next.length && !next.some((n) => n.is_primary)) next[0].is_primary = true;
    onChange(next);
  }

  function handleSetPrimary(id: string) {
    onChange(value.map((v) => ({ ...v, is_primary: v.id === id })));
  }

  return (
    <div className="space-y-3">
      {/* Existing selections */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((sel) => (
            <span
              key={sel.id}
              className={clsx(
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs",
                sel.is_primary
                  ? "border-amber-300 bg-amber-50 text-amber-900"
                  : "border-blue-200 bg-blue-50 text-blue-800",
              )}
            >
              {!singleSelect && (
                <button
                  type="button"
                  onClick={() => handleSetPrimary(sel.id)}
                  title={sel.is_primary ? "Node primer" : "Jadikan primer"}
                  disabled={disabled}
                  className={clsx(
                    "text-amber-600 hover:text-amber-700",
                    !sel.is_primary && "opacity-40",
                  )}
                >
                  <Star className="h-3.5 w-3.5" fill={sel.is_primary ? "currentColor" : "none"} />
                </button>
              )}
              <span className="truncate max-w-md">{sel.label}</span>
              <button
                type="button"
                onClick={() => handleRemove(sel.id)}
                disabled={disabled}
                className="text-gray-400 hover:text-red-600"
                title="Hapus"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Cascade dropdowns */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
        {isLoading ? (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Memuat taxonomy…
          </div>
        ) : tree.length === 0 ? (
          <p className="text-xs text-gray-500">
            Belum ada data taxonomy untuk tipe <code>{taxonomyType}</code>.
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {cascadeLevels.map((lvl, idx) => {
              const opts = childrenOf(lvl.parentId);
              if (opts.length === 0) return null;
              return (
                <div key={idx} className="flex items-center gap-1.5">
                  <select
                    value={lvl.selectedId ?? ""}
                    onChange={(e) => handlePick(idx, e.target.value)}
                    disabled={disabled}
                    className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[180px]"
                  >
                    <option value="">— Pilih level {idx + 1} —</option>
                    {opts.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.kode ? `${o.kode} · ` : ""}
                        {o.nama}
                      </option>
                    ))}
                  </select>
                  {idx < cascadeLevels.length - 1 && lvl.selectedId && (
                    <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                  )}
                </div>
              );
            })}

            <button
              type="button"
              onClick={handleAdd}
              disabled={!canAdd || disabled}
              className={clsx(
                "ml-auto inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                canAdd
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed",
              )}
            >
              <Plus className="h-3.5 w-3.5" />
              {singleSelect ? "Pilih" : "Tambah"}
            </button>
          </div>
        )}
        {leafOnly && lastPicked && lastHasChildren ? (
          <p className="mt-2 text-xs text-amber-600">
            Pilih hingga node paling spesifik (daun) untuk menambahkan.
          </p>
        ) : null}
      </div>
    </div>
  );
}
